/**
 * Online protocol shared by the Worker (GameRoom) and the browser client.
 *
 * Spec: docs/ARCHITECTURE.md, "Online protocol". This module must stay free of
 * Worker and DOM APIs: the client imports it too. Engine types are imported as
 * types only, so the client bundle never pulls the engine in through here.
 */
import type { Action, GameState } from '../engine';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Room-code alphabet: A–Z without the easily-confused I, O, L, Q (22 letters). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 4;
export const ROOM_CODE_RE = /^[ABCDEFGHJKMNPRSTUVWXYZ]{4}$/;

/** Seat limits. Keep in step with docs/RULES.md. */
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;

/** Holes per game. */
export const DEFAULT_HOLES = 9;
export const MIN_HOLES = 1;
export const MAX_HOLES = 18;

export const BOT_LEVELS = ['easy', 'medium', 'hard'] as const;
export type BotLevel = (typeof BOT_LEVELS)[number];

/**
 * Computer players' names (docs/REDESIGN.md 7.6): short, real given names from
 * around the world, used in order, skipping any already at the table. Shared
 * with the client so vs-computer games use the same pool.
 */
export const BOT_NAMES = ['Ada', 'Kofi', 'Mei', 'Ravi', 'Iris', 'Nia', 'Leif', 'Amaru', 'Yuki', 'Omar', 'Lena', 'Tane'] as const;

/** Preset reactions (Tibbs stickers): the only "chat" there is. No free text, ever. */
export const EMOTES = ['well-played', 'oof', 'smug', 'fore'] as const;
export type Emote = (typeof EMOTES)[number];

/**
 * Idle-turn clock online: a connected player has this long for a turn before
 * the room's bot plays that one turn for them. The client shows a countdown
 * for the last IDLE_WARN_MS.
 */
export const IDLE_TURN_MS = 45_000;
export const IDLE_WARN_MS = 15_000;

export const NAME_MAX_LENGTH = 20;
export const PID_RE = /^[A-Za-z0-9_-]{8,64}$/;
/** Largest client frame the server will look at, in characters. */
export const MAX_MESSAGE_CHARS = 2048;

/** Close codes the server uses when it ends a socket on purpose. */
export const CLOSE_ROOM_NOT_FOUND = 4404;
export const CLOSE_BAD_REQUEST = 4400;
export const CLOSE_ROOM_EXPIRED = 4410;

/** The exact ping frame. The GameRoom auto-responds to it without waking up. */
export const PING_FRAME = '{"t":"ping"}';
export const PONG_FRAME = '{"t":"pong"}';

/** Tibbs request keys: `h1`..`h99` (a hole's readout) or `final` (the ceremony). */
export const TIBBS_KEY_RE = /^(h[1-9][0-9]?|final)$/;

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export type ClientMessage =
  | { t: 'join'; name: string }
  | { t: 'addBot'; level: BotLevel }
  | { t: 'removeSeat'; seat: number }
  | { t: 'start'; holes: number }
  | { t: 'action'; action: Action }
  | { t: 'chat'; emote: Emote }
  | { t: 'ping' }
  // Additions beyond ARCHITECTURE.md, borrowed from the singledidge room flow:
  /** Leave the table. Lobby: frees the seat. Mid-game: a bot plays your seat until you return. */
  | { t: 'leave' }
  /** Host only, between holes: tee off now without waiting for everyone to be ready. */
  | { t: 'forceNext' }
  /** Host only: end the game (if any) and return to the lobby, dropping absent players. */
  | { t: 'backToLobby' }
  /**
   * Ask Tibbs to speak (docs/TIBBS-VOICE.md): `h<n>` is the readout of hole n,
   * `final` the Golden Amphora ceremony. The first request of a key plays for
   * the whole table in sync; later ones replay for the requester only.
   */
  | { t: 'tibbs'; key: string };

/** One seat at the table, as everyone sees it. Seat index = engine player index. */
export interface SeatView {
  name: string;
  isBot: boolean;
  botLevel?: BotLevel;
  /** Humans: at least one socket attached. Bots: always true. */
  connected: boolean;
  /** A disconnected human whose turns the server is playing with a bot. */
  autopilot: boolean;
  /** Between holes: has this player said they are ready for the next one? Bots always are. */
  ready: boolean;
  /**
   * A connected player's idle-turn clock: milliseconds left (as of this
   * message) before the room's bot plays the turn for them. Absent when no
   * clock is running for this seat.
   */
  turnMsLeft?: number;
  /** This connected player ran out of time; the room's bot is playing this one turn for them. */
  stepIn?: boolean;
}

export interface RoomView {
  code: string;
  /** 'lobby' until the host starts; 'game' once a GameState exists. */
  stage: 'lobby' | 'game';
  seats: SeatView[];
  minPlayers: number;
  maxPlayers: number;
  /**
   * Shared per game (present while stage is 'game'): seeds Tibbs's line
   * choices so every device at the table picks the same lines. Random, never
   * derived from the engine seed (which would reveal the deck).
   */
  tibbsSeed?: number;
}

export type ServerMessage =
  | {
      t: 'state';
      /** Redacted for this viewer by the engine's redactFor; null while in the lobby. */
      state: GameState | null;
      /** This socket's seat index, or null for a spectator. */
      you: number | null;
      /** Host's seat index, or null when no human holds a seat. */
      host: number | null;
      room: RoomView;
    }
  | { t: 'error'; message: string }
  | { t: 'emote'; seat: number; emote: Emote }
  /**
   * Someone asked Tibbs to speak. Play `key` at `startAt` on the room's clock
   * (`now` is the room's clock when it sent this, so delay = startAt - now).
   * `replay` is a private replay for the requester only.
   */
  | { t: 'tibbs'; key: string; seat: number | null; startAt: number; now: number; replay: boolean }
  | { t: 'pong' };

export interface CreateRoomResponse {
  code: string;
}

export interface RoomInfoResponse {
  code: string;
  stage: 'lobby' | 'game';
  players: number;
  maxPlayers: number;
  /** Whether the pid sent in the x-ancientnine-pid header holds a seat here. */
  seated: boolean;
}

/** Request header carrying the pid for GET /api/rooms/:code (kept out of URLs). */
export const PID_HEADER = 'x-ancientnine-pid';

// ---------------------------------------------------------------------------
// Validation (hand-rolled: tiny, no dependency, runs in the Worker)
// ---------------------------------------------------------------------------

export type ParseResult = { ok: true; msg: ClientMessage } | { ok: false; error: string };

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isInt = (v: unknown, min: number, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;

function hasOnlyKeys(o: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(o).every((k) => keys.includes(k));
}

/**
 * Normalize a display name: strip control and formatting characters, collapse
 * whitespace, trim, cap the length. Returns null if nothing printable is left.
 */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const cleaned = raw
    .normalize('NFC')
    .replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  const capped = Array.from(cleaned).slice(0, NAME_MAX_LENGTH).join('').trim();
  return capped.length > 0 ? capped : null;
}

export function isRoomCode(v: unknown): v is string {
  return typeof v === 'string' && ROOM_CODE_RE.test(v);
}

export function isPid(v: unknown): v is string {
  return typeof v === 'string' && PID_RE.test(v);
}

/**
 * Structural check for an engine Action: a flat object with a short `type`
 * string and optional small-integer `player` / `slot`. Whether the action is
 * legal is the engine's call; the GameRoom matches it against legalActions().
 */
export function isActionShape(v: unknown): v is Action {
  if (!isObj(v)) return false;
  if (!hasOnlyKeys(v, ['type', 'player', 'slot'])) return false;
  if (typeof v.type !== 'string' || !/^[a-zA-Z]{1,32}$/.test(v.type)) return false;
  if ('player' in v && !isInt(v.player, 0, MAX_PLAYERS - 1)) return false;
  if ('slot' in v && !isInt(v.slot, 0, 63)) return false;
  return true;
}

/** Parse and validate one raw client frame. Never throws. */
export function parseClientMessage(raw: string | ArrayBuffer): ParseResult {
  if (typeof raw !== 'string') return { ok: false, error: 'Binary frames are not supported.' };
  if (raw.length > MAX_MESSAGE_CHARS) return { ok: false, error: 'Message too large.' };
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'Malformed JSON.' };
  }
  if (!isObj(data) || typeof data.t !== 'string') return { ok: false, error: 'Missing message type.' };

  switch (data.t) {
    case 'join': {
      if (!hasOnlyKeys(data, ['t', 'name'])) break;
      const name = cleanName(data.name);
      if (!name) return { ok: false, error: 'Please choose a name.' };
      return { ok: true, msg: { t: 'join', name } };
    }
    case 'addBot': {
      if (!hasOnlyKeys(data, ['t', 'level'])) break;
      if (!BOT_LEVELS.includes(data.level as BotLevel)) return { ok: false, error: 'Unknown bot level.' };
      return { ok: true, msg: { t: 'addBot', level: data.level as BotLevel } };
    }
    case 'removeSeat': {
      if (!hasOnlyKeys(data, ['t', 'seat'])) break;
      if (!isInt(data.seat, 0, MAX_PLAYERS - 1)) return { ok: false, error: 'Bad seat.' };
      return { ok: true, msg: { t: 'removeSeat', seat: data.seat } };
    }
    case 'start': {
      if (!hasOnlyKeys(data, ['t', 'holes'])) break;
      if (!isInt(data.holes, MIN_HOLES, MAX_HOLES)) return { ok: false, error: 'Bad number of holes.' };
      return { ok: true, msg: { t: 'start', holes: data.holes } };
    }
    case 'action': {
      if (!hasOnlyKeys(data, ['t', 'action'])) break;
      if (!isActionShape(data.action)) return { ok: false, error: 'Bad action.' };
      return { ok: true, msg: { t: 'action', action: data.action } };
    }
    case 'chat': {
      if (!hasOnlyKeys(data, ['t', 'emote'])) break;
      if (!EMOTES.includes(data.emote as Emote)) return { ok: false, error: 'Unknown emote.' };
      return { ok: true, msg: { t: 'chat', emote: data.emote as Emote } };
    }
    case 'tibbs': {
      if (!hasOnlyKeys(data, ['t', 'key'])) break;
      if (typeof data.key !== 'string' || !TIBBS_KEY_RE.test(data.key)) return { ok: false, error: 'Bad Tibbs request.' };
      return { ok: true, msg: { t: 'tibbs', key: data.key } };
    }
    case 'ping':
    case 'leave':
    case 'forceNext':
    case 'backToLobby':
      if (!hasOnlyKeys(data, ['t'])) break;
      return { ok: true, msg: { t: data.t as 'ping' | 'leave' | 'forceNext' | 'backToLobby' } };
    default:
      return { ok: false, error: 'Unknown message type.' };
  }
  return { ok: false, error: 'Unexpected fields in message.' };
}

/** Generate a room code from a source of random bytes (crypto.getRandomValues). */
export function makeRoomCode(randomBytes: (n: number) => Uint8Array): string {
  const n = ROOM_CODE_ALPHABET.length; // 22
  const limit = 256 - (256 % n); // reject-sample to avoid modulo bias
  let out = '';
  while (out.length < ROOM_CODE_LENGTH) {
    for (const b of randomBytes(8)) {
      if (b < limit && out.length < ROOM_CODE_LENGTH) out += ROOM_CODE_ALPHABET[b % n];
    }
  }
  return out;
}
